/**
 * Cash & Bottom Line — the screen. Income and burn a day, the bridge from the
 * plan to the actual, cost by area, what work cost on the platform, and the
 * candidates: cuts, recoveries and escalation avoided, each with its risk.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { answerCash, buildCashDesk, CASH_MODEL_PROMPT, materialForCash, type CashDesk } from "../../domain/cash";
import { defaultRateCard } from "../../domain/decision";
import { money } from "../../domain/intelligence";
import type { ProjectRow } from "../../domain/projects";
import { Button, Card, ErrorState, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import * as store from "../decision/store";
import { useIntelligence } from "../intelligence/gather";
import { FinanceForm, useFinance } from "./FinanceForm";
import { LedgerImport } from "./LedgerImport";
import { AskCard, Bars, MonthStrip, Refs, Tiles, x as s } from "./shared";
import { useScreenContext } from "../../assistant/screen";
import { t } from "../../i18n";

function useCashRecords(enabled: boolean) {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["cash-records", siteId],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const [projects, kpis, rateCard] = await Promise.all([
        quiet(source.listProjects({ siteId }), [] as ProjectRow[]),
        quiet(source.listKpis(siteId), []),
        store.loadRateCard(source, siteId),
      ]);
      return { projects, kpis, rateCard: rateCard ?? defaultRateCard() };
    },
  });
}

export function CashPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const records = useCashRecords(!!gathered.data);
  const finance = useFinance();
  const desk = useMemo<CashDesk | null>(() => {
    if (!gathered.data || !records.data) return null;
    return buildCashDesk({
      now: new Date(),
      siteCode: session.site.code,
      finance: finance.value,
      workOrders: gathered.data.inputs.workOrders,
      liability: gathered.data.inputs.liability,
      projects: records.data.projects,
      kpis: records.data.kpis,
      rateCard: records.data.rateCard,
    });
  }, [gathered.data, records.data, finance.value, session.site.code]);
  useScreenContext(
    desk
      ? {
          route: "/cash",
          title: "Cash & Bottom Line",
          summary: desk.boardLine,
          material: materialForCash(desk),
          figures: desk.figures,
          own: (q) => answerCash(q, desk),
          prompt: CASH_MODEL_PROMPT,
          offers: [
            { label: "What do we burn a day?", ask: "what do we burn a day?" },
            { label: "What is on the table?", ask: "what is on the table to cut or recover?" },
            { label: "Where is the plan missed?", ask: "where is the plan missed?" },
          ],
        }
      : null,
  );
  return (
    <Page
      /* moved out of Executive with the rest of the money, 2026-09-21 */
      eyebrow={t("{site} · Financial System", { site: session.site.code })}
      title="Cash & Bottom Line"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        {t(
          "Daily income and spending, where the plan is missed, and where money can be saved.",
        )}
      </p>
      {(gathered.isPending || records.isPending) && <Skeleton rows={8} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {desk && (
        <div className={s.page}>
          {desk.basis === "demo" && (
            <p className={s.muted}>
              <b>{t("Demonstration figures.")}</b>{" "}
              {t(
                "Sample figures until the finance system is connected.",
              )}{" "}
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  const d = document.getElementById("finance-drawer") as HTMLDetailsElement | null;
                  if (d) d.open = true;
                  document.getElementById("finance-ledger")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {t("Connect the ledger")}
              </Button>
            </p>
          )}
          <Tiles items={desk.tiles} currency={desk.currency} />
          {finance.savedTo && <p className={s.saved}>{finance.savedTo}</p>}
          <div className={s.two}>
            <Card title="Revenue, cost and plan by month" sub="blue revenue, red cost, gray the revenue plan">
              <MonthStrip months={finance.value.monthly} currency={desk.currency} />
            </Card>
            <Card
              title="From the plan to the bottom line"
              sub={t("plan net {plan} → actual {actual}", {
                plan: money(Math.round(desk.bridge.budgetNet), desk.currency),
                actual: money(Math.round(desk.bridge.actualNet), desk.currency),
              })}
            >
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <tbody>
                    {desk.bridge.steps.map((st) => (
                      <tr key={st.label}>
                        <td>
                          {st.label}
                          <Refs list={st.sources} />
                        </td>
                        <td className={`${s.num} ${st.amount >= 0 ? s.pos : s.neg}`}>
                          {st.amount >= 0 ? "+" : "−"}
                          {money(Math.abs(Math.round(st.amount)), desk.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
          <div className={s.two}>
            <Card title="Operating cost by area" sub="actual against budget for the period">
              {desk.byArea.length ? (
                <Bars
                  rows={desk.byArea.map((a) => ({ label: a.area, value: a.actual, against: a.budget }))}
                  currency={desk.currency}
                />
              ) : (
                <p className={s.muted}>{t("No split by area entered.")}</p>
              )}
            </Card>
            <Card
              title="What work cost on the platform"
              sub={t("{n} priced work orders — actual against planned", { n: desk.workSpend.orders })}
            >
              {desk.workSpend.byDiscipline.length ? (
                <Bars
                  rows={desk.workSpend.byDiscipline.map((b) => ({
                    label: `${b.discipline} (${b.orders})`,
                    value: b.actual,
                    against: b.planned,
                    href: "/work-orders",
                  }))}
                  currency={desk.currency}
                />
              ) : (
                <p className={s.muted}>{t("No work order on the platform carries a cost yet.")}</p>
              )}
            </Card>
          </div>
          <Card
            title="On the table"
            sub="Savings available, largest first"
          >
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>{t("kind")}</th>
                    <th>{t("candidate")}</th>
                    <th>{t("a year")}</th>
                    <th>{t("working")}</th>
                    <th>{t("risk")}</th>
                  </tr>
                </thead>
                <tbody>
                  {desk.candidates.map((c) => (
                    <tr key={c.key} data-testid={`candidate-${c.key}`}>
                      <td>
                        <span className={c.kind === "cut" ? s.tagRed : c.kind === "recover" ? s.tagGreen : s.tagBlue}>
                          {c.kind === "cut" ? t("cut") : c.kind === "recover" ? t("recover") : t("avoid")}
                        </span>
                      </td>
                      <td>
                        <b>{c.label}</b>
                        <div className={s.muted}>{c.area}</div>
                        <Refs list={c.sources} />
                      </td>
                      <td className={s.num}>
                        {money(c.perYear, desk.currency)}
                        {c.basis === "starting" && <div className={s.tag}>{t("demo / entered")}</div>}
                      </td>
                      <td>{c.working}</td>
                      <td>{c.risk}</td>
                    </tr>
                  ))}
                  {desk.candidates.length === 0 && (
                    <tr>
                      <td colSpan={5} className={s.muted}>
                        {t("Nothing on the table: every line is on plan and no priced work escalates.")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className={s.muted}>
              {t("The capital projects have their own screen:")} <Link to="/capex">{t("Capital Projects")}</Link>.{" "}
              {t("The deferred-cost register is at")} <Link to="/liability">{t("Liability")}</Link>.
            </p>
          </Card>
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            board={desk.boardLine}
            material={materialForCash(desk)}
            figures={desk.figures}
            own={(q) => answerCash(q, desk)}
            prompt={CASH_MODEL_PROMPT}
            placeholder={t("What do we burn a day? What is on the table? Where is the plan missed?")}
          />
          {/* Open while the figures are still the demonstration's. A call to
              action folded inside a collapsed drawer is a call nobody hears —
              this was reported as "I cannot find it", which is the same thing
              said politely. Once a ledger is connected it collapses again,
              because then it is a settings panel rather than the next step. */}
          <details className={s.drawer} id="finance-drawer" open={finance.value.basis === "demo"}>
            <summary>{t("Finance figures: connect SAP or enter them by hand")}</summary>
            <div className={s.drawerBody} id="finance-ledger">
              <LedgerImport
                finance={finance.value}
                who={session.principal.name}
                onApply={(f) => void finance.save(f)}
              />
              {!finance.loading && <FinanceForm key={finance.at ?? "seed"} finance={finance.value} onSave={(f) => void finance.save(f)} />}
            </div>
          </details>
          {desk.gaps.length > 0 && (
            <Card title="What this desk does not cover" sub="so the reader knows what is absent">
              <ul className={s.plain}>
                {desk.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}
